import { Divider, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Card } from '@/components/layout/Card/Card';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useFormContext } from '@/components/react-hook-form';
import {
  DisplayTable,
  DisplayTableColumn,
  StyledTableCell,
} from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { entityKindToDisplayName } from '@/modules/entities/EntityForm/utils';
import { COLORS } from '@/styles/tokens/colors';
import {
  IncomeTaxPaymentConfigGrantorPaymentMethod,
  IncomeTaxPaymentConfigNonGrantorPaymentMethod,
} from '@/types/schema';
import { formatPercent } from '@/utils/formatting/percent';
import { getNodes } from '@/utils/graphqlUtils';

import { useGrowthProfileModalContext } from '../GrowthProfileModal.context';
import { GrowthProfileModalFormShape } from '../GrowthProfileModal.types';
import { mapTaxDragEntity } from './TaxDrag.entityUtils';
import { useTaxDragQuery } from './TaxDrag.generated';
import { TaxDragBaseMode } from './TaxDrag.types';
import { getDisplayedTaxDragRate } from './TaxDrag.utils';

const RATE_COLUMNS: [DisplayTableColumn, DisplayTableColumn] = [
  { headerName: 'Asset category', flex: 1 },
  { headerName: 'Tax drag %', align: 'right', width: 160 },
];

function getBadgeLabel(
  overrideCount: number,
  baseMode: TaxDragBaseMode
): string {
  if (overrideCount > 0) return 'Custom';
  if (baseMode === TaxDragBaseMode.Default) return 'Default';
  return 'Global';
}

function getGrantorSummary(
  paymentMethod: IncomeTaxPaymentConfigGrantorPaymentMethod,
  customPayerOrder: string[]
): string {
  if (paymentMethod === IncomeTaxPaymentConfigGrantorPaymentMethod.ProRata) {
    return 'Pro rata';
  }
  if (customPayerOrder.length > 0) return 'Custom order';
  return 'Default (custom order)';
}

function RateTable({
  rows,
  entityRows = false,
}: {
  rows: Array<{
    id: string;
    label: string;
    description?: string;
    rate: Decimal | null;
  }>;
  entityRows?: boolean;
}) {
  return (
    <DisplayTable
      variant="plain"
      columns={
        entityRows
          ? [{ ...RATE_COLUMNS[0], headerName: 'Entity' }, RATE_COLUMNS[1]]
          : RATE_COLUMNS
      }
    >
      {rows.map(({ id, label, description, rate }) => (
        <StyledTableRow key={id}>
          <StyledTableCell>
            <Typography variant="body-xs">{label}</Typography>
            {description && (
              <Typography
                component="div"
                variant="body-xs"
                sx={{ color: COLORS.TEXT.SECONDARY, mt: 0.5 }}
              >
                {description}
              </Typography>
            )}
          </StyledTableCell>
          <StyledTableCell align="right">
            <Typography variant="body-xs">
              {rate == null ? '—' : `${formatPercent(rate, 2)}%`}
            </Typography>
          </StyledTableCell>
        </StyledTableRow>
      ))}
    </DisplayTable>
  );
}

interface TaxDragSummaryCardProps {
  onEdit: () => void;
}

export function TaxDragSummaryCard({ onEdit }: TaxDragSummaryCardProps) {
  const { householdId, assetClasses, isDefault } =
    useGrowthProfileModalContext();
  const { control } = useFormContext<GrowthProfileModalFormShape>();
  const [taxDrag] = useWatch({ control, name: ['taxDrag'] });
  const [showDetails, setShowDetails] = useState(false);
  const { data } = useTaxDragQuery({ variables: { householdId } });
  const entitiesById = useMemo(
    () =>
      new Map(
        getNodes(data?.entities)
          .map(mapTaxDragEntity)
          .map((entity) => [entity.id, entity])
      ),
    [data?.entities]
  );

  const defaultRateRows = assetClasses.map((assetClass) => ({
    id: assetClass.id,
    label: assetClass.displayName,
    rate: assetClass.taxDragRate,
  }));
  const overrideRateRows = taxDrag.overrides.map((override) => {
    const entity = entitiesById.get(override.entityId);
    return {
      id: override.entityId,
      label: entity?.displayName ?? 'Unknown entity',
      description: entity ? entityKindToDisplayName(entity.kind) : undefined,
      rate: getDisplayedTaxDragRate(override),
    };
  });
  const grantorSummary = getGrantorSummary(
    taxDrag.grantorPaymentMethod,
    taxDrag.customPayerOrder
  );
  const nonGrantorSummary =
    taxDrag.nonGrantorPaymentMethod ===
    IncomeTaxPaymentConfigNonGrantorPaymentMethod.FollowGrantor
      ? 'Follow grantor method'
      : 'Default (self)';
  const globalRate = getDisplayedTaxDragRate(taxDrag.globalRate);

  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: 2 }}
      data-testid="tax-drag-card"
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="title-m">Tax drag</Typography>
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
            The share of annual investment return assumed to be paid as taxes on
            investment income. Tax drag is applied to grantor trusts,
            non-grantor trusts, and directly-held entities. By default, this
            profile inherits the asset-level tax drag rates set at the firm
            level.
          </Typography>
        </Stack>
        <Divider />
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Button
            variant="secondary"
            size="xs"
            startIcon={Edit02Icon}
            onClick={onEdit}
            disabled={isDefault}
          >
            Edit tax drag
          </Button>
          <Badge
            variant={BadgeVariants.Accent}
            display={getBadgeLabel(taxDrag.overrides.length, taxDrag.baseMode)}
            wrapperSx={{ display: 'flex', alignItems: 'center' }}
            sx={{ py: '1px' }}
          />
          <Button
            variant="transparent"
            size="xs"
            endIcon={ChevronDownIcon}
            onClick={() => setShowDetails((current) => !current)}
            sx={{
              ml: 'auto',
              '& svg': {
                transform: showDetails ? 'rotate(180deg)' : undefined,
              },
            }}
          >
            {showDetails ? 'Hide details' : 'View details'}
          </Button>
        </Stack>

        {showDetails && (
          <Stack spacing={2}>
            <Divider />
            {taxDrag.baseMode === TaxDragBaseMode.Global && (
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="title-s">Global tax drag</Typography>
                <Typography variant="body-xs">
                  Enables override of entity level tax drag rates from the asset
                  category defaults set at the firm level.
                </Typography>
                <Typography
                  variant="title-xl"
                  sx={{
                    bgcolor: COLORS.GRAY[50],
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  {globalRate == null
                    ? '—'
                    : `${formatPercent(globalRate, 2)}%`}
                </Typography>
              </Stack>
            )}
            {overrideRateRows.length > 0 && (
              <Stack spacing={1}>
                {taxDrag.baseMode === TaxDragBaseMode.Global && (
                  <Divider sx={{ mb: 1 }} />
                )}
                <Typography variant="title-s">
                  Individual entity tax drag customization
                </Typography>
                <Typography variant="body-xs">
                  The below entities have a custom tax drag rate applied, which
                  differs from the{' '}
                  {taxDrag.baseMode === TaxDragBaseMode.Global
                    ? 'global tax drag above'
                    : 'asset category defaults'}
                  .
                </Typography>
                <RateTable rows={overrideRateRows} entityRows />
              </Stack>
            )}
            {taxDrag.baseMode === TaxDragBaseMode.Default && (
              <Stack spacing={1}>
                {overrideRateRows.length > 0 && <Divider sx={{ mb: 1 }} />}
                <Typography variant="title-s">
                  Default tax drag rates
                </Typography>
                <RateTable rows={defaultRateRows} />
              </Stack>
            )}
            <Divider />
            <Stack
              spacing={1}
              sx={{
                '& .MuiTypography-body-xs': {
                  color: COLORS.TEXT.PRIMARY,
                  fontWeight: 600,
                },
              }}
            >
              <Typography variant="title-s">Tax paying entity</Typography>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body-xs">
                  Grantor and directly held entities:
                </Typography>
                <Typography variant="body-xs">{grantorSummary}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body-xs">Non-grantor trusts:</Typography>
                <Typography variant="body-xs">{nonGrantorSummary}</Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
